import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { filter, firstValueFrom, take } from 'rxjs';
import { LoginService } from '../../../services/login-service';

/**
 * Landing route for the Auth0 redirect (`/callback?code=...&state=...`).
 *
 * The SDK does the code-for-token exchange itself, keyed off the query string
 * rather than the route, so this component's only jobs are to hold the screen
 * while that completes and then send the user somewhere real -- without it the
 * router has no match for /callback and the user lands on a blank page holding
 * a perfectly valid session.
 *
 * Deliberately has no guards on its route: it must be reachable while still
 * unauthenticated, since that is the whole point of it.
 */
@Component({
  selector: 'app-callback',
  standalone: false,
  template: `
    <div class="d-flex flex-column align-items-center justify-content-center"
         style="height: 100vh; gap: 1rem;">
      <mat-progress-spinner mode="indeterminate" diameter="48"></mat-progress-spinner>
      <div>Signing you in...</div>
    </div>
  `,
})
export class CallbackComponent implements OnInit {
  /**
   * Where to land after sign-in. AuthGuard passes the originally-requested URL
   * through Auth0 as `appState.target`, so a user who deep-linked to a guarded
   * page gets back to it rather than the home page.
   */
  private target?: string;

  constructor(
    private auth: AuthService,
    private router: Router,
    private loginService: LoginService
  ) {
    // Subscribed in the constructor because appState$ fires as part of the
    // redirect-callback handling, which can complete before ngOnInit runs.
    // If it never emits we simply fall back to '/'.
    this.auth.appState$
      .pipe(take(1))
      .subscribe((state: any) => {
        if (typeof state?.target === 'string') this.target = state.target;
      });
  }

  async ngOnInit(): Promise<void> {
    await firstValueFrom(this.auth.isLoading$.pipe(filter(loading => !loading)));

    const isAuthenticated = await firstValueFrom(this.auth.isAuthenticated$);

    if (!isAuthenticated) {
      // The exchange failed -- AppComponent's error$ subscription will have
      // logged the reason. Send the user somewhere that explains itself rather
      // than looping back into a login redirect.
      await this.router.navigateByUrl('/login-failed');
      return;
    }

    try {
      await this.loginService.ensureUserLoaded();
    } catch {
      // Same failure UserHydrationGuard handles for every other route -- but
      // this is the very first stop after a fresh interactive login, reached
      // before any guard runs. Without this, a rejection here (e.g. the 401
      // WellChron.Server returns for an account with no WellChron access role)
      // throws out of ngOnInit and the navigateByUrl below never runs, leaving
      // the user stuck on this component's own "Signing you in..." template.
      await this.router.navigateByUrl('/login-failed');
      return;
    }

    await this.router.navigateByUrl(this.target ?? '/');
  }
}
